/**
 * `ask` — a question, answered down one ladder (D231; `docs/design/actors.md`
 * §4, the half that needs no actor).
 *
 * @remarks
 * A question has an ADDRESSEE (who is asked — the direct object) and a
 * SUBJECT (what about — the `about` phrase), each optional. The answer is the
 * first rung that speaks:
 *
 * 1. **A topic.** The addressee's `topics`, matched against the subject
 *    (`liveTopicFor`: first in declared order, keys matching, `when`
 *    holding, not spent). No addressee named, and the things present that
 *    declare topics are asked in catalog order, the room last — `ask about
 *    the directive` reaches whoever here knows. A topic's lines speak, its
 *    `once` is spent in the player's ledger, and its effects fold as a `use`
 *    interaction's do.
 * 2. **The one-topic answer.** The subject's own `askNarratives` — R1.1's
 *    dossier shape, D137's for rooms: what is said about THIS thing, whoever
 *    is asked. With nothing asked about (`ask the voice`, `talk to the
 *    voice`, E10), the addressee's own: a question about them — and for an
 *    ACTOR addressed (D232), its `greetingNarratives`: what it says when
 *    spoken to — and then (D235, ruling 6's sugar door) the prompt its
 *    `opensWith` names is ASKED, when its options leave anything to ask and
 *    it has not been asked already. An actor addressed ABOUT a thing never falls through to the
 *    thing's `askNarratives`: that dossier line is another voice's (the
 *    POC's are EURYDICE's), and a person asked about the mask does not
 *    answer in the narrator's words. Only the actor's own table, its
 *    fallback, and the deflection speak for it.
 * 3. **The addressee's fallback.** `topicFallbackNarratives`, only when the
 *    player addressed a thing that has topics and none of them, nor the
 *    subject, had an answer — "Ask me something I know." Never for an
 *    implicit addressee: a thing nobody asked does not get to say that.
 * 4. **The deflection.** The `askDeflection` slot (design doc §3.3): it
 *    parsed, it is legal, she declines. The handwritten log's missing entry
 *    MUST keep falling through to here — do not "fix" it.
 *
 * Topics outrank `askNarratives` everywhere, explicit addressee or not, so a
 * one-voice pack's `ask about X` and `ask the voice about X` agree: a topic
 * is the more particular answer (keyed, conditional, ordered), and an author
 * who keys one on a thing that already has a dossier line has chosen.
 *
 * @packageDocumentation
 */

import type { ActorId, NarrativeLine, RuntimeState } from '@orpheus/content-schema';

import type { AskParty, AskSubject, RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor } from '../bundle/bundle-lookups.js';
import { askPrompt } from '../conversation/prompts.js';
import { liveTopicFor, presentTopicBearers, type TopicBearer, topicBearerOf, withTopicSpent } from '../conversation/topics.js';
import { speakSlot } from '../defaults/default-responses.js';
import { deriveEntityCatalog } from '../entities/derive-entity-catalog.js';
import { type ActionHandlerResult, completedSpeaking, type SpeakableLine } from './action-handler-result.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';
import { withEffects } from './with-effects.js';

/**
 * Answer a question.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed `ask`
 * @param cursor - This turn's transcript cursor
 * @returns The completed answer — a question is never refused
 */
export const handleAsk = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'ask' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const { addressee, subject } = action;
	const explicitBearer = addressee === undefined ? undefined : topicBearerOf(bundle, addressee);

	// Rung 1 — a topic, from whoever was asked, or from whoever here knows.
	const answeredByATopic = subject === undefined ? undefined : answerFromATopic(bundle, state, cursor, explicitBearer, subject);

	if (answeredByATopic !== undefined) {
		return answeredByATopic;
	}

	// D235: `talk to <actor>` — the greeting, then the opening prompt. The
	// greeting is the one-topic answer below for an actor addressed alone;
	// what the door adds is the ask, which decides for itself whether there
	// is anything to ask (spent, or nothing offerable — then the greeting
	// stands alone, as it did in round A).
	if (subject === undefined && addressee?.kind === 'actor') {
		return openingWith(bundle, state, cursor, addressee.actorId);
	}

	// Rung 2 — the one-topic answer about the subject, or about the addressee
	// when nothing was asked about. D232: an actor asked ABOUT a thing is not
	// answered by the thing's dossier line — that is another voice.
	const askedAbout: AskParty | undefined =
		subject === undefined ? addressee : subject.kind === 'word' || addressee?.kind === 'actor' ? undefined : subject;
	const oneTopicAnswer = askedAbout === undefined ? undefined : oneTopicAnswerOf(bundle, askedAbout);

	if (askedAbout !== undefined && oneTopicAnswer !== undefined) {
		return completedSpeaking(bundle, state, cursor, oneTopicAnswer, askContextOf(askedAbout));
	}

	// Rung 3 — the addressee's own fallback, when they were addressed and have a table.
	if (explicitBearer?.topics !== undefined && explicitBearer.topicFallbackNarratives !== undefined) {
		return completedSpeaking(bundle, state, cursor, explicitBearer.topicFallbackNarratives, askContextOf(subject ?? explicitBearer.party));
	}

	// Rung 4 — the deflection, stamped as the asked-about thing's occurrence
	// when there is one (the context every pack has carried since R1.1).
	return completedSpeaking(
		bundle,
		state,
		cursor,
		[speakSlot(bundle, state, 'askDeflection')],
		askedAbout === undefined ? 'ask:deflection' : askContextOf(askedAbout),
	);
};

/**
 * Rung 1: the first topic that answers the subject, from the one addressed or
 * from whoever here knows (extracted at the complexity cap when D235 gave the
 * handler its door).
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param cursor - This turn's transcript cursor
 * @param explicitBearer - The addressee's table, when somebody was addressed
 * @param subject - What was asked about
 * @returns The completed answer, or `undefined` when no topic speaks
 */
const answerFromATopic = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	explicitBearer: TopicBearer | undefined,
	subject: AskSubject,
): ActionHandlerResult | undefined => {
	const bearers = explicitBearer === undefined ? presentTopicBearers(bundle, deriveEntityCatalog(bundle, state)) : [explicitBearer];

	for (const bearer of bearers) {
		const topic = bearer.topics === undefined ? undefined : liveTopicFor(bundle, state, bearer.topics, subject);

		if (topic !== undefined) {
			// Spent before it speaks, so the lines are stamped against the
			// state that already remembers them being said.
			const spent = withTopicSpent(state, topic);
			// Stamped as the ASK it answers, so the Voices index hears one act
			// whether a topic or a dossier line spoke (R33's rule for `use`).
			const spoken = stampNarrativeLines(bundle, spent, cursor, topic.narratives, askContextOf(subject));

			return withEffects(bundle, { cursor: spoken.cursor, entries: spoken.entries, state: spent, type: 'completed' }, topic.effects);
		}
	}

	return undefined;
};

/**
 * D235: an actor addressed with nothing asked about — its greeting, down the
 * same rungs as every answer (the greeting, else its table's fallback when
 * it has a table, else the deflection), and then the prompt its `opensWith`
 * names, asked through the one door every ask goes through.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param cursor - This turn's transcript cursor
 * @param actorId - The actor spoken to
 * @returns The completed greeting, the question parked when there was one to ask
 */
const openingWith = (bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor, actorId: ActorId): ActionHandlerResult => {
	const actor = requireActor(bundle, actorId);
	const greeting: readonly SpeakableLine[] =
		actor.greetingNarratives ?? (actor.topics === undefined ? undefined : actor.topicFallbackNarratives) ?? [speakSlot(bundle, state, 'askDeflection')];
	const greeted = completedSpeaking(bundle, state, cursor, greeting, askContextOf({ actorId, kind: 'actor' }));

	if (actor.opensWith === undefined) {
		return greeted;
	}

	const asking = askPrompt(bundle, greeted.state, greeted.cursor, actorId, actor.opensWith);

	return { ...greeted, cursor: asking.cursor, entries: [...greeted.entries, ...asking.entries], state: asking.state };
};

/**
 * A party's one-topic answer: its `askNarratives` — what is said about it.
 * An actor asked about by someone else answers with its dossier line like
 * any thing; an actor ADDRESSED with nothing asked about never reaches here
 * (D232's greeting is `openingWith`'s, with D235's door after it).
 *
 * @param bundle - The loaded bundle
 * @param party - The thing, room or actor the answer is about
 * @returns Its lines, or `undefined` when it authors none (absence is silence)
 */
const oneTopicAnswerOf = (bundle: EngineGameBundle, party: AskParty): undefined | readonly NarrativeLine[] => {
	switch (party.kind) {
		case 'actor': {
			return bundle.indexes.actorsById.get(party.actorId)?.askNarratives;
		}
		case 'item': {
			return bundle.indexes.itemsById.get(party.itemId)?.askNarratives;
		}
		case 'room': {
			return bundle.indexes.roomsById.get(party.roomId)?.askNarratives;
		}
	}
};

/**
 * The transcript context an answer about a subject is stamped under — the
 * contexts every pack has carried since R1.1 and D137, unchanged, and a
 * topic word under `ask:` by the word.
 *
 * @param subject - The thing, room or word the answer is about
 * @returns `ask:<itemId>`, `ask:<actorId>`, `ask_room:<roomId>` or `ask:<word>`
 */
const askContextOf = (subject: AskSubject): string => {
	switch (subject.kind) {
		// D232: an actor under `ask:` by its id, as a thing is — one act, whoever answered.
		case 'actor': {
			return `ask:${subject.actorId}`;
		}
		case 'item': {
			return `ask:${subject.itemId}`;
		}
		case 'room': {
			return `ask_room:${subject.roomId}`;
		}
		case 'word': {
			return `ask:${subject.word}`;
		}
	}
};
